import { useEffect } from 'react';
import { Box, Alert, Text } from '@nimbus-ds/components';
import { useParams } from 'react-router-dom';
import { OrderDetailsResponseDto } from '@tiendanube/common';
import { useOrdersEmailTracking } from 'domains/Orders/Orders/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useOrderIsEdited } from '../../../hooks';
import InsufficientStockAlert from '../InsufficientStockAlert';
import RefundStatusBanner from '../RefundStatusBanner';

interface AlertsProps {
  orderDetails: OrderDetailsResponseDto | null;
}

function Alerts({ orderDetails }: Readonly<AlertsProps>): JSX.Element | null {
  const { id } = useParams<{ id: string }>();
  const t = useTranslationOrders();

  const { showAlert: showEmailsAlert, fetchBouncedEmails } =
    useOrdersEmailTracking(id);

  const isEdited = useOrderIsEdited();

  const inconsistencyAlertKey = isEdited ? 'edit' : 'alert';

  const showUnclaimedStockAlert = !!orderDetails?.products.some(
    (product) =>
      !!product.issues &&
      Object.keys(product.issues).some((issue) => issue === 'unclaimedStock'),
  );

  const insufficientStockProducts =
    orderDetails?.products
      .filter((p) => p.issues?.unclaimedStock)
      .map((p) => ({
        id: p.id,
        title: p.title,
        variants: p.variants,
      })) || [];

  useEffect(() => {
    fetchBouncedEmails();
  }, [fetchBouncedEmails]);

  if (!orderDetails) return null;
  return (
    <div className="cont-message-alert">
      <Box gap="3" display="flex" flexDirection="column">
        <RefundStatusBanner orderDetails={orderDetails} />
        <Alert
          appearance="warning"
          title={t('emailsTracking.alert.title')}
          show={showEmailsAlert}
        >
          <Text>{t('emailsTracking.alert.message')}</Text>
        </Alert>
        <Alert
          appearance="warning"
          title={t(`inconsistentProducts.${inconsistencyAlertKey}.title`)}
          show={!!orderDetails.inconsistentProducts}
        >
          <Text>
            {t(`inconsistentProducts.${inconsistencyAlertKey}.message`)}
          </Text>
        </Alert>
        <InsufficientStockAlert
          products={insufficientStockProducts}
          show={showUnclaimedStockAlert}
        />
      </Box>
    </div>
  );
}

export default Alerts;
